// Image preloading utilities for optimized loading

/**
 * Preload an image by creating a new Image object
 * @param src - Image source URL
 * @returns Promise that resolves when image is loaded
 */
export function preloadImage(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Preload multiple images concurrently
 * @param sources - Array of image source URLs
 * @returns Promise that resolves when all images are loaded
 */
export function preloadImages(sources: string[]): Promise<void[]> {
  return Promise.all(sources.map(src => preloadImage(src)));
}

/**
 * Preload critical images that are likely to be used soon
 * This should be called early in the application lifecycle
 */
export function preloadCriticalImages() {
  const criticalImages = [
    "/images/logo_neo.png",
    "/images/neo_avatar.png",
    "/images/logo_Allonia.png",
    "/favicon-32x32.png",
    "/android-chrome-192x192.png",
  ];

  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePreload = (callback: () => void) => {
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      requestIdleCallback(callback);
    } else {
      setTimeout(callback, 100);
    }
  };

  schedulePreload(() => {
    preloadImages(criticalImages)
      .then(() => {
        // Critical images preloaded successfully
      })
      .catch(error => {
        console.warn("Some critical images failed to preload:", error);
      });
  });
}

/**
 * Intersection Observer for lazy loading images when they enter viewport
 * @param element - HTML element to observe
 * @param callback - Function to call when element enters viewport
 * @param options - IntersectionObserver options
 */
export function createIntersectionObserver(
  element: Element,
  callback: () => void,
  options: IntersectionObserverInit = {
    threshold: 0.1,
    rootMargin: "50px",
  }
): IntersectionObserver {
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        callback();
        observer.unobserve(entry.target);
      }
    });
  }, options);

  observer.observe(element);
  return observer;
}

/**
 * Create a resource hint link element for preloading
 * @param href - Resource URL
 * @param as - Resource type (image, script, style, etc.)
 * @param rel - Relationship (preload, prefetch, etc.)
 */
export function createResourceHint(href: string, as: string, rel = "preload") {
  if (typeof document === "undefined") return null;

  const link = document.createElement("link");
  link.rel = rel;
  link.as = as;
  link.href = href;

  if (as === "image") {
    link.crossOrigin = "anonymous";
  }

  document.head.appendChild(link);
  return link;
}

/**
 * Remove a resource hint link element
 * @param link - Link element to remove
 */
export function removeResourceHint(link: HTMLLinkElement | null) {
  if (link && link.parentNode) {
    link.parentNode.removeChild(link);
  }
}

/**
 * Batch preload images with progressive loading
 * @param imageUrls - Array of image URLs to preload
 * @param batchSize - Number of images to preload at once
 * @param delay - Delay between batches in milliseconds
 */
export async function batchPreloadImages(
  imageUrls: string[],
  batchSize = 3,
  delay = 100
): Promise<void> {
  for (let i = 0; i < imageUrls.length; i += batchSize) {
    const batch = imageUrls.slice(i, i + batchSize);

    try {
      await preloadImages(batch);
      // Preloaded batch successfully
    } catch (error) {
      console.warn(`Batch preload failed for:`, batch, error);
    }

    // Add delay between batches if not the last batch
    if (i + batchSize < imageUrls.length) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}
